<template>
  <div class="shell-page purchase-status-page">
    <HeaderMain @messageFromHeader="searchEvents" />
    <SearchCourse v-if="!main" :courses="search" />

    <section class="purchase-status-section">
      <div class="purchase-status-card purchase-status-card--hero">
        <div class="purchase-status-hero">
          <div class="purchase-status-hero__copy">
            <span class="section-heading__eyebrow">Compra confirmada</span>
            <h2 class="section-heading__title">Tus entradas ya están listas.</h2>
            <p class="section-heading__text">
              Todo salió bien. Tu compra quedó aprobada y el link de descarga ya está activo.
            </p>
          </div>

          <div class="purchase-status-hero__actions">
            <button class="btn btn-primary purchase-status-button purchase-status-button--primary" type="button" :disabled="isDownloadButtonDisabled" @click="downloadT">
              <i class="fas fa-download"></i>
              {{ downloadActionLabel }}
            </button>
            <button class="btn btn-outline-light purchase-status-button" type="button" @click="copyLink">
              <i class="fas fa-link"></i>
              Copiar descarga
            </button>
            <button class="btn btn-outline-light purchase-status-button purchase-status-button--share" type="button" @click="shareWhatsApp">
              <i class="fab fa-whatsapp"></i>
              Estado WA
            </button>
            <button class="btn btn-outline-light purchase-status-button purchase-status-button--share" type="button" @click="shareInstagram">
              <i class="fab fa-instagram"></i>
              Historia IG
            </button>
          </div>
        </div>

        <div class="purchase-status-steps">
          <article class="purchase-status-step is-done">
            <strong>01</strong>
            <span>Compra confirmada</span>
            <small>Pago aprobado</small>
          </article>
          <article class="purchase-status-step is-done">
            <strong>02</strong>
            <span>Link activo</span>
            <small>Descarga disponible</small>
          </article>
          <article class="purchase-status-step is-done">
            <strong>03</strong>
            <span>Revisa tu correo</span>
            <small>También queda en Mis Tickets</small>
          </article>
        </div>

        <div class="purchase-status-highlights">
          <span>{{ category.name }}</span>
          <span>{{ venue.name }}{{ city ? ' · ' + city : '' }}</span>
          <span>{{ formatDate(event.start_date) }}</span>
          <span>Link de descarga activo</span>
        </div>

        <div class="summary purchase-status-summary">
          <h3>{{ event.name }}</h3>
          <p>
            Si no lo ves enseguida en tu correo, revisa spam o promociones. Tu descarga queda disponible aquí y luego en <strong>Mis Tickets</strong>.
          </p>
          <p v-if="isStripePdfLoading" class="purchase-status-feedback">Estamos preparando el PDF final de esta compra para que descargues el mismo archivo del flujo clásico.</p>
          <p v-if="copyMessage" class="purchase-status-feedback">{{ copyMessage }}</p>

          <va-alert
            v-if="event.id === 1"
            style="margin-top: 22px;"
            description="Este evento tiene una fiesta de cierre exclusiva. Haz click aqui para descubrirla."
            @click="link"
          />
        </div>

        <div v-if="shouldRenderStripePdfGenerator" class="purchase-status-pdf-builder">
          <PdfGeneration
            :ticketsTable="stripePdfTickets"
            :event="event"
            :invoice="stripeInvoiceNumber"
            @messageFromPdfGeneration="handleStripePdfGenerated"
          />
        </div>
      </div>
    </section>

    <FooterMain />
  </div>
</template>

<script>
import { apiService, apiServiceForm } from '../common/api.services.js'
import HeaderMain from '@/components/HeaderMain.vue'
import FooterMain from '@/components/FooterMain.vue'
import PdfGeneration from '@/components/sales/PdfGeneration.vue'
import SearchCourse from '@/components/SearchCourse.vue'
import { formatEventDate } from '@/common/event-timezone.js'

export default {
  name: 'ConfirmationView',

  props: {
    id: {
      required: true,
    },
  },

  components: {
    HeaderMain,
    FooterMain,
    PdfGeneration,
    SearchCourse,
  },

  data() {
    return {
      search: [],
      searchField: '',
      main: true,
      event: {},
      venue: {},
      category: {},
      city: '',
      copyMessage: '',
      downloadLinkFallback: '',
      stripeInvoiceData: null,
      stripePdfUrl: '',
      stripePdfLoading: false,
      stripePdfRenderKey: 0,
      generatedTicketFile: null,
    }
  },

  created() {
    this.getEvent()
    this.prepareStripePdfIfNeeded()
  },

  methods: {
    searchEvents(results, term) {
      this.search = results
      this.searchField = term
      this.main = term === ''
    },

    getEvent() {
      apiService(`/api/events/${this.id}/`).then((data) => {
        this.event = data
        this.category = data.category || {}
        this.venue = data.venue || {}
        this.city = (
          (data.venue && data.venue.city && typeof data.venue.city === 'object' && data.venue.city.name) ||
          (data.city_detail && data.city_detail.name) ||
          (data.city && typeof data.city === 'object' && data.city.name) ||
          ''
        )
      })

      if (!this.$route.query.co && this.$route.query.attempt && this.cardInvoiceProvider) {
        const params = new URLSearchParams({ attempt_token: this.$route.query.attempt })
        if (this.cardInvoiceProvider === 'stripe' && this.$route.query.payment_intent) {
          params.set('payment_intent_id', this.$route.query.payment_intent)
        }
        if (this.cardInvoiceProvider === 'rebill' && this.$route.query.rebill_payment) {
          params.set('payment_id', this.$route.query.rebill_payment)
        }

        const endpoint = this.cardInvoiceProvider === 'rebill' ? '/api/rebill/payment-status/' : '/api/stripe/payment-status/'
        apiService(`${endpoint}?${params.toString()}`).then((data) => {
          if (data && !data.err && data.ticket_url && (!this.cardInvoiceProvider || this.isHostedTicketUrl(data.ticket_url))) {
            this.downloadLinkFallback = data.ticket_url
          }
        })
      }
    },

    readStripePdfCache(invoiceId) {
      if (typeof window === 'undefined' || !invoiceId) {
        return null
      }

      const cache = window.__fomoStripePdfCache || {}
      return cache[String(invoiceId)] || null
    },

    readStoredStripeDownloadUrl(invoiceId) {
      if (typeof window === 'undefined' || !invoiceId) {
        return ''
      }

      return window.sessionStorage.getItem(`fomoStripeTicketUrl:${invoiceId}`) || ''
    },

    storeStripePdfCache(invoiceId, file) {
      if (typeof window === 'undefined' || !invoiceId || !file) {
        return ''
      }

      const cacheKey = String(invoiceId)
      const existingCache = window.__fomoStripePdfCache || {}
      const existingEntry = existingCache[cacheKey]
      if (existingEntry && existingEntry.url) {
        URL.revokeObjectURL(existingEntry.url)
      }

      const url = URL.createObjectURL(file)
      window.__fomoStripePdfCache = {
        ...existingCache,
        [cacheKey]: {
          url,
          name: `FomoTicket-${cacheKey}.pdf`,
          createdAt: Date.now(),
        },
      }

      return url
    },

    wait(ms) {
      return new Promise((resolve) => window.setTimeout(resolve, ms))
    },

    async uploadGeneratedTicketPdf(file, attemptToken) {
      const formData = new FormData()
      formData.append('attempt_token', attemptToken)
      formData.append('ticket', file)
      const endpoint = this.cardInvoiceProvider === 'rebill' ? '/api/rebill/attach-ticket/' : '/api/stripe/attach-ticket/'
      return apiServiceForm(endpoint, 'POST', formData)
    },

    async fetchGeneratedTicketUrl(attemptToken) {
      if (!attemptToken) {
        return ''
      }

      const params = new URLSearchParams({ attempt_token: attemptToken })
      if (this.cardInvoiceProvider === 'rebill' && this.$route.query.rebill_payment) {
        params.set('payment_id', this.$route.query.rebill_payment)
      }
      if (this.cardInvoiceProvider === 'stripe' && this.$route.query.payment_intent) {
        params.set('payment_intent_id', this.$route.query.payment_intent)
      }

      const endpoint = this.cardInvoiceProvider === 'rebill' ? '/api/rebill/payment-status/' : '/api/stripe/payment-status/'
      const data = await apiService(`${endpoint}?${params.toString()}`)
      if (!data || data.err || !data.ticket_url) {
        return ''
      }

      return this.cardInvoiceProvider && !this.isHostedTicketUrl(data.ticket_url) ? '' : data.ticket_url
    },

    isHostedTicketUrl(value) {
      const raw = String(value || '').trim()
      if (!raw || raw.startsWith('blob:') || raw.includes('/user-tickets/')) {
        return false
      }

      try {
        const url = new URL(raw, window.location.origin)
        return (
          url.hostname === 'gingopolisdjango.blob.core.windows.net'
          || url.pathname.startsWith('/media/')
          || url.pathname.startsWith('/fomoapp/media/')
        ) && url.pathname.toLowerCase().endsWith('.pdf')
      } catch (_error) {
        return raw.startsWith('/media/') && raw.toLowerCase().endsWith('.pdf')
      }
    },

    prepareStripePdfIfNeeded() {
      if (!this.isStripeInvoiceFlow || !this.stripeInvoiceNumber) {
        return
      }

      if (!this.cardInvoiceProvider) {
        const cachedPdf = this.readStripePdfCache(this.stripeInvoiceNumber)
        if (cachedPdf && cachedPdf.url) {
          this.stripePdfUrl = cachedPdf.url
          this.stripePdfLoading = false
          return
        }
      }

      const storedDownloadUrl = this.readStoredStripeDownloadUrl(this.stripeInvoiceNumber)
      if (storedDownloadUrl) {
        if (!this.cardInvoiceProvider || this.isHostedTicketUrl(storedDownloadUrl)) {
          this.downloadLinkFallback = storedDownloadUrl
          this.stripePdfLoading = false
          return
        }
      }

      this.stripePdfLoading = true
      apiService(`/api/invoices/${this.stripeInvoiceNumber}/`).then((data) => {
        if (!data || data.err || !data.id) {
          this.stripePdfLoading = false
          return
        }

        this.stripeInvoiceData = data
        this.stripePdfRenderKey += 1
      }).catch(() => {
        this.stripePdfLoading = false
      })
    },

    async handleStripePdfGenerated(payload) {
      const file = payload && typeof payload === 'object' && payload.file ? payload.file : payload
      if (!file) {
        this.stripePdfLoading = false
        return
      }

      this.generatedTicketFile = file

      const attemptToken = this.$route.query.attempt || ''
      if (attemptToken) {
        const maxAttempts = this.cardInvoiceProvider ? 6 : 1
        for (let index = 0; index < maxAttempts; index += 1) {
          const uploadedTicket = await this.uploadGeneratedTicketPdf(file, attemptToken)
          if (uploadedTicket && !uploadedTicket.err && uploadedTicket.ticket_url && (!this.cardInvoiceProvider || this.isHostedTicketUrl(uploadedTicket.ticket_url))) {
            this.stripePdfUrl = uploadedTicket.ticket_url
            this.downloadLinkFallback = uploadedTicket.ticket_url
            this.stripePdfLoading = false
            return
          }

          const existingTicketUrl = await this.fetchGeneratedTicketUrl(attemptToken)
          if (existingTicketUrl) {
            this.downloadLinkFallback = existingTicketUrl
            this.stripePdfLoading = false
            return
          }

          if (index < maxAttempts - 1) {
            await this.wait(1000)
          }
        }

        if (this.cardInvoiceProvider) {
          this.copyMessage = 'Estamos terminando de subir tu PDF a Azure. Intenta descargar de nuevo en unos segundos.'
          this.stripePdfLoading = false
          return
        }
      }

      this.stripePdfUrl = this.storeStripePdfCache(this.stripeInvoiceNumber, file)
      this.stripePdfLoading = false
    },

    normalizeDownloadLink(rawLink) {
      const value = String(rawLink || '').trim()
      if (!value) {
        return ''
      }

      try {
        const normalized = new URL(value, window.location.origin)
        if (['fomoplanes.com', 'www.fomoplanes.com'].includes(normalized.hostname) && normalized.pathname.startsWith('/user-tickets/')) {
          return `${window.location.origin}${normalized.pathname}${normalized.search}`
        }

        return normalized.toString()
      } catch (_error) {
        return value
      }
    },

    downloadT() {
      if (this.isStripePdfLoading) {
        this.copyMessage = 'Estamos terminando de preparar tu PDF.'
        return
      }

      if (this.cardInvoiceProvider && !this.downloadLink) {
        this.refreshEmbeddedDownloadLink()
        return
      }

      if (!this.downloadLink) {
        this.copyMessage = 'No encontramos el link de descarga todavía.'
        return
      }

      if (this.isTicketPortalLink) {
        window.location.assign(this.downloadLink)
        return
      }

      window.open(this.downloadLink, '_blank')
    },

    async refreshEmbeddedDownloadLink() {
      const attemptToken = this.$route.query.attempt || ''
      this.copyMessage = 'Buscando tu ticket en Azure...'

      if (attemptToken && this.generatedTicketFile) {
        const uploadedTicket = await this.uploadGeneratedTicketPdf(this.generatedTicketFile, attemptToken)
        if (uploadedTicket && !uploadedTicket.err && uploadedTicket.ticket_url && this.isHostedTicketUrl(uploadedTicket.ticket_url)) {
          this.downloadLinkFallback = uploadedTicket.ticket_url
          this.copyMessage = ''
          window.open(this.downloadLink, '_blank')
          return
        }
      }

      const ticketUrl = await this.fetchGeneratedTicketUrl(attemptToken)
      if (ticketUrl) {
        this.downloadLinkFallback = ticketUrl
        this.copyMessage = ''
        window.open(this.downloadLink, '_blank')
        return
      }

      this.copyMessage = 'El ticket aun se esta subiendo a Azure. Intenta de nuevo en unos segundos.'
    },

    copyLink() {
      if (this.isBlobDownloadLink) {
        this.copyMessage = 'El PDF ya esta listo. Descargalo desde este dispositivo con el boton principal.'
        return
      }

      const link = this.downloadLink

      if (!link) {
        this.copyMessage = 'No encontramos el link de descarga todavía.'
        return
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(link).then(() => {
          this.copyMessage = 'Link copiado.'
        })
        return
      }

      this.copyMessage = 'Tu navegador no permitió copiar automáticamente, pero el link sigue disponible aquí.'
    },

    shareWhatsApp() {
      if (!this.shareUrl) {
        this.copyMessage = 'No encontramos el link público del evento todavía.'
        return
      }

      this.shareToStatus('whatsapp')
    },

    shareInstagram() {
      if (!this.shareUrl) {
        this.copyMessage = 'No encontramos el link público del evento todavía.'
        return
      }

      this.shareToStatus('instagram')
    },

    shareToStatus(platform) {
      const networkLabel = platform === 'whatsapp' ? 'tu estado de WhatsApp' : 'tu historia de Instagram'
      const shareText = `Voy a ${this.event.name || 'este evento'} en FOMO`

      if (navigator.share) {
        navigator.share({
          title: this.event.name || 'FOMO',
          text: shareText,
          url: this.shareUrl,
        }).then(() => {
          this.copyMessage = `Se abrió el menú para compartir. Elige ${networkLabel} si tu dispositivo lo permite.`
        }).catch(() => {
          this.copyShareLink(networkLabel)
        })
        return
      }

      this.copyShareLink(networkLabel)
    },

    copyShareLink(networkLabel) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(this.shareUrl).then(() => {
          this.copyMessage = `Link copiado. Ahora pégalo en ${networkLabel}.`
        })
        return
      }

      this.copyMessage = `Copia este link y pégalo en ${networkLabel}.`
    },

    link() {
      window.location.replace('https://www.fomoplanes.com/event/BYE-TOQUE/?co=fomoupsale1')
    },

    formatDate(dateString) {
      return formatEventDate(dateString, this.event, {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      })
    },
  },

  computed: {
    downloadLink() {
      const localGeneratedUrl = this.cardInvoiceProvider ? '' : this.stripePdfUrl
      const candidate = localGeneratedUrl || this.normalizeDownloadLink(this.$route.query.co || this.downloadLinkFallback || '')
      if (this.cardInvoiceProvider && !this.isHostedTicketUrl(candidate)) {
        return ''
      }

      return candidate
    },

    isDownloadButtonDisabled() {
      if (this.isStripePdfLoading) {
        return true
      }

      if (this.cardInvoiceProvider) {
        return false
      }

      return !this.downloadLink
    },

    cardInvoiceProvider() {
      const provider = String(this.$route.query.provider || '').toLowerCase()
      return ['stripe', 'rebill'].includes(provider) ? provider : ''
    },

    isStripeInvoiceFlow() {
      return Boolean(this.cardInvoiceProvider) && Boolean(this.$route.query.invoice) && !this.$route.query.co
    },

    stripeInvoiceNumber() {
      return Number.parseInt(this.$route.query.invoice || '0', 10) || 0
    },

    stripePdfTickets() {
      return this.stripeInvoiceData && Array.isArray(this.stripeInvoiceData.invoice_items)
        ? this.stripeInvoiceData.invoice_items
        : []
    },

    shouldRenderStripePdfGenerator() {
      return this.isStripeInvoiceFlow
        && this.stripePdfLoading
        && this.stripePdfTickets.length > 0
        && this.event
        && this.event.id
    },

    isStripePdfLoading() {
      return this.isStripeInvoiceFlow && this.stripePdfLoading && !this.stripePdfUrl
    },

    isBlobDownloadLink() {
      return String(this.downloadLink || '').startsWith('blob:')
    },

    isTicketPortalLink() {
      if (this.cardInvoiceProvider) {
        return false
      }

      return this.downloadLink.includes('/user-tickets/')
    },

    downloadActionLabel() {
      if (this.isStripePdfLoading) {
        return 'Preparando PDF...'
      }

      if (this.cardInvoiceProvider) {
        return 'Descargar entradas'
      }

      return this.isTicketPortalLink ? 'Abrir mis tickets' : 'Descargar entradas'
    },

    shareUrl() {
      if (!this.event || !this.event.slug) {
        return ''
      }

      return `${window.location.origin}/event/${this.event.slug}`
    },
  },
}
</script>

<style scoped>
.purchase-status-page {
  background:
    radial-gradient(circle at top right, rgba(123, 128, 156, 0.08), transparent 24%),
    linear-gradient(180deg, rgba(10, 12, 18, 1), rgba(7, 9, 14, 1));
}

.purchase-status-section {
  width: min(100%, 1160px);
  margin: 0 auto;
  padding: 42px 20px 72px;
}

.purchase-status-card--hero {
  overflow: hidden;
  display: grid;
  gap: 24px;
  padding: 34px;
  border-radius: 34px;
  background:
    radial-gradient(circle at top right, rgba(120, 126, 151, 0.06), transparent 24%),
    linear-gradient(180deg, rgba(18, 20, 30, 0.98), rgba(10, 12, 18, 0.98));
  border: 1px solid rgba(198, 205, 220, 0.1);
  box-shadow: 0 26px 58px rgba(0, 0, 0, 0.24);
}

.purchase-status-hero {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: flex-end;
}

.purchase-status-hero__copy {
  max-width: 760px;
}

.purchase-status-hero__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: flex-end;
}

.purchase-status-button {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 52px;
  padding: 0 20px;
  border-radius: 999px;
}

.purchase-status-button--primary {
  background: linear-gradient(135deg, #272c36, #414957) !important;
  border-color: transparent !important;
}

.purchase-status-button--share {
  min-width: 158px;
  justify-content: center;
}

.purchase-status-steps {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.purchase-status-step {
  position: relative;
  display: grid;
  gap: 8px;
  padding: 16px 18px 16px 22px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(198, 205, 220, 0.08);
}

.purchase-status-step::before {
  content: '';
  position: absolute;
  inset: 14px auto 14px 0;
  width: 3px;
  border-radius: 999px;
  background: rgba(142, 147, 170, 0.3);
}

.purchase-status-step strong {
  color: rgba(187, 193, 209, 0.92);
  font-size: 0.74rem;
  font-family: 'Space Grotesk', sans-serif;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.purchase-status-step span {
  color: #eef2f7;
  font-size: 1.05rem;
}

.purchase-status-step small {
  color: rgba(186, 194, 208, 0.72);
}

.purchase-status-step.is-done {
  background: linear-gradient(180deg, rgba(22, 25, 36, 0.96), rgba(16, 18, 28, 0.96));
}

.purchase-status-step.is-done::before {
  background: linear-gradient(180deg, rgba(130, 136, 163, 0.82), rgba(103, 112, 141, 0.7));
}

.purchase-status-highlights {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.purchase-status-highlights span {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 999px;
  border: 1px solid rgba(198, 205, 220, 0.09);
  background: rgba(255, 255, 255, 0.03);
  color: #d9dfeb;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.78rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.purchase-status-summary {
  padding-top: 8px;
  border-top: 1px solid rgba(198, 205, 220, 0.08);
}

.purchase-status-summary h3 {
  color: #eef2f7;
}

.purchase-status-summary p {
  color: rgba(203, 210, 224, 0.82);
  line-height: 1.65;
}

.purchase-status-feedback {
  color: #d7deeb !important;
}

.purchase-status-pdf-builder {
  width: 0;
  height: 0;
  overflow: hidden;
}

@media (max-width: 900px) {
  .purchase-status-hero {
    display: grid;
  }

  .purchase-status-hero__actions {
    justify-content: flex-start;
  }

  .purchase-status-steps {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .purchase-status-section {
    padding: 24px 12px 56px;
  }

  .purchase-status-card--hero {
    padding: 22px 18px;
    border-radius: 26px;
  }

  .purchase-status-hero__actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .purchase-status-button {
    justify-content: center;
    width: 100%;
    padding: 0 14px;
  }

  .purchase-status-button--primary {
    grid-column: 1 / -1;
  }

  .purchase-status-button--share {
    min-width: 0;
  }

  .purchase-status-highlights span {
    width: 100%;
    justify-content: center;
    text-align: center;
  }
}
</style>
